import type { ReactNode } from 'react';

import type { ListingApplicationPayload } from '#/api/ats';
import type { ApiListingApplicationListingType } from '#/api/types/ApiListingApplicationListingType';
import type { ApiRequestedAttachment } from '#/api/types/ApiRequestedAttachment';
import type { ApiS3FileId } from '#/api/types/ApiTypedId';
import { Box } from '#/components/Box/Box';
import { CheckboxField } from '#/components/Form/CheckboxField';
import { useFormContext } from '#/components/Form/FormContext';
import { HiddenField } from '#/components/Form/HiddenField';
import { InputField } from '#/components/Form/InputField';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { useEnvironment, useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';

import { ListingRequestedAttachmentsFieldSwitch } from './ListingRequestedAttachmentsFieldSwitch';
import type { RenderRequestedAttachmentFileFieldFn } from './ListingRequestedAttachmentsFieldSwitch';

export type RenderListingApplicationResumeField = (data: {
  name: string;
  label: string;
  disabled: boolean;
  description: string;
  accept: string;
  isNewResume: boolean;
  extraAnalyticsData: Record<string, unknown>;
  onChange: (fileId: ApiS3FileId | null) => void;
  onError: (error: Error, fileId: ApiS3FileId | null) => void;
}) => ReactNode;

type Props = {
  listingType: ApiListingApplicationListingType;
  disabled: boolean;
  allFieldsRequired: boolean;
  requestedAttachments: ApiRequestedAttachment[];
  initialResumeFileId: ApiS3FileId | null;
  renderRequestedAttachmentFileField: RenderRequestedAttachmentFileFieldFn;
  renderResumeField: RenderListingApplicationResumeField;
};

export function ListingAtsApplicationFields({
  listingType,
  disabled,
  allFieldsRequired,
  requestedAttachments,
  initialResumeFileId,
  renderRequestedAttachmentFileField,
  renderResumeField,
}: Props) {
  const { i18n } = useI18n();
  const { user } = useUser();
  const { environment } = useEnvironment();

  const userCountry =
    user?.location?.countryCode || environment.guessedLocation.countryCode;

  const { handleError, values, updateValues, clearErrors } =
    useFormContext<ListingApplicationPayload>();

  const isNewResume =
    initialResumeFileId &&
    values.resumeS3FileId &&
    values.resumeS3FileId !== initialResumeFileId;

  return (
    <>
      <InputField
        disabled={disabled}
        name="firstName"
        label={
          !allFieldsRequired
            ? `${i18n('First Name')}*`
            : `${i18n('First Name')}`
        }
        placeholder={i18n('Casey')}
      />

      <InputField
        disabled={disabled}
        name="lastName"
        label={
          !allFieldsRequired ? `${i18n('Last Name')}*` : `${i18n('Last Name')}`
        }
        placeholder={i18n('Smith')}
      />

      <InputField
        disabled={disabled}
        name="email"
        label={!allFieldsRequired ? `${i18n('Email')}*` : `${i18n('Email')}`}
        type="email"
        placeholder={i18n('casey.smith@example.com')}
      />

      {listingType !== 'VOLOP' &&
        renderResumeField({
          name: 'resumeS3FileId',
          label: !allFieldsRequired
            ? `${i18n('Resume')}*`
            : `${i18n('Resume')}`,
          description: i18n('Resume must be uploaded in PDF format'),
          disabled,
          extraAnalyticsData: {
            context: 'Listing Application Form',
            listing_type: listingType,
            file_label: 'Resume',
          },
          accept: '.pdf',
          isNewResume: Boolean(isNewResume),
          onChange: (fileId) => {
            updateValues({ resumeS3FileId: fileId });
            clearErrors();
          },
          onError: (error, fileId) => {
            handleError(error, { resumeS3FileId: fileId });
          },
        })}

      {isNewResume && (
        <Box mt={-26}>
          <CheckboxField
            disabled={disabled}
            name="replaceCurrentResume"
            label={
              <Box display="flex" alignItems="center">
                {i18n('Replace my current resume on file with this one')}
                <Box position="relative" zIndex={1} mt="1px" ml="6px">
                  <LegacyTooltip>
                    {i18n(`You already have a resume saved with your account.
                              If you check this box, we'll save this new resume
                              to your account.`)}
                  </LegacyTooltip>
                </Box>
              </Box>
            }
          />
        </Box>
      )}

      <HiddenField name="requestedAttachmentsHash" />
      {requestedAttachments.map((requestedAttachment, index) => (
        <ListingRequestedAttachmentsFieldSwitch
          // oxlint-disable-next-line no-array-index-key
          key={index}
          index={index}
          requestedAttachment={requestedAttachment}
          renderRequestedAttachmentFileField={
            renderRequestedAttachmentFileField
          }
          allFieldsRequired={allFieldsRequired}
          disabled={disabled}
          defaultCountry={userCountry}
        />
      ))}
    </>
  );
}
